import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Skeleton,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
  Stack,
} from '@mui/material';
import { monitoramentoService } from '../../../services/api';
import ScreenSearchDesktopIcon from '@mui/icons-material/ScreenSearchDesktop';
import GroupSearchIcon from '@mui/icons-material/PersonSearch';
import BiotechIcon from '@mui/icons-material/Biotech';
import HealingIcon from '@mui/icons-material/Healing';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ShieldIcon from '@mui/icons-material/Shield';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

interface CicloItem {
  tipo_acao: string;
  acoes_distintas: number;
  total_executado: number;
  meta_total: number;
  percentual_atingimento: number;
}

interface AcaoItem {
  tipo_acao: string;
  grupo_cid: string;
  nome_acao: string;
  descricao: string;
  responsavel_principal: string;
  total_executado: number;
  meta_periodo: number;
  percentual_atingimento: number;
  populacao_alvo: number;
  meses_no_periodo: number;
}

interface Props {
  municipioId: number;
  dataInicio: string;
  dataFim: string;
}

const TIPOS_ORDEM = ['RASTREIO', 'BUSCA_ATIVA', 'DIAGNOSTICO', 'TRATAMENTO', 'ACOMPANHAMENTO', 'PREVENCAO'];

const TIPO_LABEL: Record<string, string> = {
  RASTREIO: 'Rastreio',
  BUSCA_ATIVA: 'Busca Ativa',
  DIAGNOSTICO: 'Diagnostico',
  TRATAMENTO: 'Tratamento',
  ACOMPANHAMENTO: 'Acompanhamento',
  PREVENCAO: 'Prevencao',
};

const TIPO_COR: Record<string, string> = {
  RASTREIO: '#1976d2',
  BUSCA_ATIVA: '#f57c00',
  DIAGNOSTICO: '#7b1fa2',
  TRATAMENTO: '#388e3c',
  ACOMPANHAMENTO: '#00838f',
  PREVENCAO: '#5d4037',
};

const TIPO_ICON: Record<string, any> = {
  RASTREIO: ScreenSearchDesktopIcon,
  BUSCA_ATIVA: GroupSearchIcon,
  DIAGNOSTICO: BiotechIcon,
  TRATAMENTO: HealingIcon,
  ACOMPANHAMENTO: EventRepeatIcon,
  PREVENCAO: ShieldIcon,
};

const CicloAssistencialSection: React.FC<Props> = ({ municipioId, dataInicio, dataFim }) => {
  const [ciclo, setCiclo] = useState<CicloItem[]>([]);
  const [acoes, setAcoes] = useState<AcaoItem[]>([]);
  const [grupos, setGrupos] = useState<any[]>([]);
  const [grupoFiltro, setGrupoFiltro] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [c, a, g] = await Promise.all([
          monitoramentoService.getParametrosCicloAssistencial(municipioId, dataInicio, dataFim, grupoFiltro || undefined),
          monitoramentoService.getParametrosAcoesAssistenciais(municipioId, dataInicio, dataFim, undefined, grupoFiltro || undefined),
          monitoramentoService.getVigilanciaGruposCid(municipioId),
        ]);
        if (!cancelled) {
          setCiclo(c);
          setAcoes(a);
          setGrupos(g);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.message || 'Erro ao carregar acoes assistenciais.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [municipioId, dataInicio, dataFim, grupoFiltro]);

  const cicloOrdenado = useMemo(() => {
    const map = new Map(ciclo.map(c => [c.tipo_acao, c]));
    return TIPOS_ORDEM.map(t => map.get(t) || {
      tipo_acao: t, acoes_distintas: 0, total_executado: 0, meta_total: 0, percentual_atingimento: 0,
    });
  }, [ciclo]);

  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Ciclo de Acoes Assistenciais (RF-12.3)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Rastreio → Busca Ativa → Diagnostico → Tratamento → Acompanhamento → Prevencao
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Grupo CID</InputLabel>
          <Select value={grupoFiltro} label="Grupo CID" onChange={(e) => setGrupoFiltro(e.target.value)}>
            <MenuItem value=""><em>Todos os grupos</em></MenuItem>
            {grupos.map((g: any) => (
              <MenuItem key={g.codigo_grupo} value={g.codigo_grupo}>{g.nome}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Fluxo de etapas */}
      <Grid container spacing={1} alignItems="stretch" sx={{ mb: 2 }}>
        {cicloOrdenado.map((c, idx) => {
          const Icon = TIPO_ICON[c.tipo_acao] || ScreenSearchDesktopIcon;
          const cor = TIPO_COR[c.tipo_acao] || '#1976d2';
          const pct = Math.min(Number(c.percentual_atingimento || 0), 100);
          return (
            <React.Fragment key={c.tipo_acao}>
              <Grid item xs={12} sm={6} md={2}>
                {loading ? <Skeleton variant="rounded" height={140} /> : (
                  <Paper variant="outlined" sx={{ p: 1.5, borderTop: `4px solid ${cor}`, height: '100%' }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                      <Icon sx={{ color: cor }} fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={700}>{TIPO_LABEL[c.tipo_acao]}</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {c.acoes_distintas} acao(es)
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                      {Number(c.total_executado).toLocaleString('pt-BR')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Meta: {Number(c.meta_total).toLocaleString('pt-BR')}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { backgroundColor: cor } }}
                      />
                      <Typography variant="caption" sx={{ mt: 0.3, display: 'block' }}>
                        {pct.toFixed(0)}% da meta
                      </Typography>
                    </Box>
                  </Paper>
                )}
              </Grid>
              {idx < TIPOS_ORDEM.length - 1 && (
                <Grid item xs="auto" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                  <ArrowRightAltIcon sx={{ color: 'text.disabled' }} />
                </Grid>
              )}
            </React.Fragment>
          );
        })}
      </Grid>

      {/* Tabela detalhada */}
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell>Grupo CID</TableCell>
              <TableCell>Acao</TableCell>
              <TableCell>Responsavel</TableCell>
              <TableCell align="right">Executado</TableCell>
              <TableCell align="right">Meta</TableCell>
              <TableCell align="right">% atingimento</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7}><Skeleton /></TableCell></TableRow>
            )}
            {!loading && acoes.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Sem acoes para o filtro selecionado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {acoes.map((a, i) => {
              const pct = Math.min(Number(a.percentual_atingimento || 0), 999);
              const cor = TIPO_COR[a.tipo_acao] || '#1976d2';
              return (
                <TableRow key={i} hover>
                  <TableCell>
                    <Chip size="small" label={TIPO_LABEL[a.tipo_acao]} sx={{ bgcolor: cor, color: 'white', fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell>{a.grupo_cid}</TableCell>
                  <TableCell>{a.nome_acao}</TableCell>
                  <TableCell>{a.responsavel_principal}</TableCell>
                  <TableCell align="right">{Number(a.total_executado).toLocaleString('pt-BR')}</TableCell>
                  <TableCell align="right">{Number(a.meta_periodo).toLocaleString('pt-BR')}</TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      label={`${pct.toFixed(0)}%`}
                      color={pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error'}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};

export default CicloAssistencialSection;
